import ReCAPTCHA from "react-google-recaptcha";
import styled from "styled-components";
import { PUBLIC_KEY, RE_CAPTCHA_SITE_KEY } from "../../../constants/accessTokens";
import { contactValidate } from "../../../validations/contactValidate";
import { buildGeoDataForSubmit } from "../../../utils/drawUtils";
import { useRef, useState } from "react";
import { useMap } from "../../../hooks/useMap";
import { useDraw } from "../../../hooks/useDraw";

const isDev = process.env.REACT_APP_NODE_ENV === "development";

export default function FormContainer({ isIncludeGeoData }) {
  const map = useMap();
  const draw = useDraw();
  
  const [values, setValues] = useState({
    name: "",
    email: "",
    message: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [capVal, setCapVal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = contactValidate(values);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      try {
        setIsLoading(true);

        const body = {
          name: values.name,
          email: values.email,
          message: values.message,
          ...(isDev ? {} : { tokenCapcha: capVal }),
          ...(isIncludeGeoData ? { geoData: map && draw ? buildGeoDataForSubmit(map, draw) : draw ? draw.getAll() : null } : {}),
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/email/send-message`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": PUBLIC_KEY,
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errorData = await response.json();

          setErrors((prev) => ({ ...prev, submit: errorData.error || "An error occurred. Please try again." }));
        } else {
          setValues((prev) => ({ ...prev, name: "", email: "", message: "" }));
          setErrors({});
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrors((prev) => ({ ...prev, submit: "An error occurred. Please try again." }));
      } finally {
        setIsLoading(false);
        setCapVal(null);
        if (recaptchaRef.current) recaptchaRef.current.reset();
      }
    }
  };

  return (
    <FormContainerStyled onSubmit={handleSubmit}>
      <InputsContainer>
        <div className="input-group">
          <label>Name</label>
          <input type="text" name="name" value={values.name} onChange={handleChange} />
          {errors.name && <span className="error">{errors.name}</span>}
        </div>
        <div className="input-group">
          <label>Email</label>
          <input type="text" name="email" value={values.email} onChange={handleChange} />
          {errors.email && <span className="error">{errors.email}</span>}
        </div>
        <div className="input-group">
          <label>Message (optional)</label>
          <textarea name="message" rows="3" value={values.message} onChange={handleChange}></textarea>
        </div>
      </InputsContainer>
      <ConsertContainer>
        <input type="checkbox" id="consent" name="consent" required value={values.consent} onChange={handleChange} />
        <label htmlFor="consent">
          Privacy Notice: The personal data you provide (such as name, email address, geographical input) will be used solely to process your request. The legal basis is
          your consent pursuant to Art. 6 (1) lit. a GDPR. For more information, including your rights, please see our{" "}
          <a href="https://hozesolutions.com/data-protection/" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </label>
      </ConsertContainer>
      {!isDev && (
        <CaptchaContainer>
          <ReCAPTCHA ref={recaptchaRef} sitekey={RE_CAPTCHA_SITE_KEY} onChange={(e) => setCapVal(e)} />
        </CaptchaContainer>
      )}
      <SubbmitContainer>
        <SubbmitButton type="submit" disabled={(!isDev && !capVal) || !values.consent}>
          {isLoading ? "Sending..." : "Submit"}
        </SubbmitButton>
        {errors.submit && <span className="error">{errors.submit}</span>}
      </SubbmitContainer>
    </FormContainerStyled>
  );
}

const FormContainerStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .error {
    color: red;
    font-size: 12px;
    margin-top: 2px;
  }

  .submit-btn {
    all: unset;
    cursor: pointer;
    border: 2px solid rgba(0, 0, 255, 1);
    padding: 10px 40px;
    border-radius: 5px;
    color: rgba(0, 0, 255, 1);
    font-weight: 700;
    text-align: center;
    transition: background-color 0.3s ease, color 0.3s ease;
    &:hover {
      background-color: rgba(0, 0, 255, 1);
      color: #fff;
    }
    &:disabled {
      border-color: #ccc;
      color: #ccc;
      cursor: not-allowed;
      &:hover {
        background-color: transparent;
        color: #ccc;
      }
    }
  }
`;

const SubbmitContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SubbmitButton = styled.button`
  all: unset;
  cursor: pointer;
  border: 2px solid rgba(0, 0, 255, 1);
  padding: 10px 40px;
  border-radius: 5px;
  color: rgba(0, 0, 255, 1);
  font-weight: 700;
  text-align: center;
  transition: background-color 0.3s ease, color 0.3s ease;
  &:hover {
    background-color: rgba(0, 0, 255, 1);
    color: #fff;
  }
  &:disabled {
    border-color: #ccc;
    color: #ccc;
    cursor: not-allowed;
    &:hover {
      background-color: transparent;
      color: #ccc;
    }
  }
`;

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .input-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    label {
      font-weight: 600;
      color: #555;
    }
    input,
    textarea {
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
      &:focus {
        border-color: rgba(0, 0, 255, 1);
        outline: none;
      }
    }
    textarea {
      resize: vertical;
    }
  }
`;

const CaptchaContainer = styled.div`
  margin: 0 auto;
  height: 78px;
`;

const ConsertContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  line-height: 1.4;
`;
